import {
  Component,
  inject,
  InjectionToken,
  OnInit,
  output,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import {
  DynamicFormField,
  fieldGroup,
  html,
  input,
  repeatTable,
  select,
} from '@kinet-open-network/client/core/forms/ui';
import {
  AutomationTrigger,
  AutomationTriggerConfig,
  IAutomationData,
} from '@kinet-open-network/shared/data-access/model';
import { FormlyModule } from '@ngx-formly/core';

export const TRIGGER_DATA_TEMPLATE = `
  @if(fields; as dataFields) {
    <formly-form 
      [fields]="dataFields"
      [model]="model"
      (modelChange)="handleModelChange($event)"
    />
  } @else {
    <p class="text-gray-400">Žiadne argumenty</p>
  }
`;

export const AUTOMATION_TOKEN = new InjectionToken<IAutomationData>(
  'Automation',
);

@Component({
  selector: 'kon-automation-trigger',
  imports: [FormlyModule],
  template: TRIGGER_DATA_TEMPLATE,
})
export class TriggerComponent<T extends AutomationTrigger> implements OnInit {
  readonly automation = inject<IAutomationData<T>>(AUTOMATION_TOKEN);

  triggerChange = output<AutomationTriggerConfig<T>>();

  defaultModel: Record<string, unknown> = {};

  model: Record<string, unknown> = {};

  fields: DynamicFormField[] | null = null;

  handleModelChange(data: Record<string, unknown>): void {
    this.triggerChange.emit({
      ...this.automation.triggerData,
      ...this.modelToData(data),
    } as AutomationTriggerConfig<T>);
  }

  ngOnInit(): void {
    this.model = {
      ...Object.entries(
        this.dataToModel(this.automation.triggerData || {}),
      ).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value;
        }
        return acc;
      }, this.defaultModel),
    };
  }

  protected preferredTimesFields = [
    fieldGroup()
      .wrappers(['form-field'])
      .props({
        label: 'Preferované časy',
      })
      .fieldGroupClassName('p-1.5 border rounded')
      .fieldGroup([
        html(
          '<p class="text-sm text-gray-500">Ak udalosť nastane pred týmto časom, automatizácia sa spustí až v najbližsom preferovanom čase (maximálne však do 24 hodín). Napríklad, pokiaľ sú preferované časy nastavené na 08:00, 12:00 a 18:00 a udalosť nastane o 7:30, automatizácia sa spustí o 08:00. Pokiaľ udalosť nastane o 13:00, automatizace se spustí o 18:00. Pokiaľ udalosť nastane o 19:00, automatizácia sa spustí o 08:00 nasledujúceho dňa.</p>',
        ),
        repeatTable('preferredTimes')
          .array(
            fieldGroup().fieldGroup([
              input('time', 'Čas')
                .validators({
                  isValidTime: {
                    expression: (control: AbstractControl) => {
                      if (!control.value) {
                        return true;
                      }
                      const timePattern = /^([01]\d|2[0-3]):([0-5]\d)$/; // HH:mm formát
                      return timePattern.test(control.value);
                    },
                    message: 'Zadejte platný čas vo formáte HH:mm',
                  },
                })
                .props({
                  placeholder: 'HH:mm',
                }),
              select('timezone', 'Časová zóna')
                .options(
                  Intl.supportedValuesOf('timeZone').map((tz) => ({
                    label: tz,
                    value: tz,
                  })),
                )
                .defaultValue(Intl.DateTimeFormat().resolvedOptions().timeZone),
            ]),
          )
          .props({
            headerCellClass: 'text-left',
            newItem: (curr) => ({
              time: '',
              preferredTimezone:
                curr.preferredTimezone ||
                Intl.DateTimeFormat().resolvedOptions().timeZone,
            }),
          }),
      ]),
  ];

  protected dataToModel(
    data: Partial<AutomationTriggerConfig<T>>,
  ): Record<string, unknown> {
    return data as Record<string, unknown>;
  }

  protected modelToData(
    model: Record<string, unknown>,
  ): Partial<AutomationTriggerConfig<T>> {
    return model as unknown as AutomationTriggerConfig<T>;
  }
}
